import FldCellBlock, { FldTotalBlock } from './FldCellBlock';

/** PBG / CG label pairs for row headers */
const PBG_ROW_LABELS: Record<string, { pbg: string; cg: string }> = {
  HISPANIC: { pbg: 'Hispanic', cg: 'Not Hispanic' },
  AMERICAN_INDIAN: { pbg: 'Am. Indian', cg: 'White' },
  ASIAN: { pbg: 'Asian', cg: 'White' },
  BLACK: { pbg: 'Black', cg: 'White' },
  NATIVE_HAWAIIAN: { pbg: 'Nat. Hawaiian', cg: 'White' },
  FEMALE: { pbg: 'Female', cg: 'Male' },
  MALE_FEMALE_JOINT: { pbg: 'Male & Female', cg: 'Male' },
  ELDERLY: { pbg: 'Elderly', cg: 'Not Elderly' },
  MMCT: { pbg: 'MMCT', cg: 'NMCT' },
};

const PURPOSE_LABELS: Record<string, string> = {
  HOME_PURCHASE: 'Home Purch.',
  HOME_IMPROVEMENT: 'Home Imp.',
  REFINANCING: 'Refi',
  CASH_OUT_REFI: 'Cash-out',
  OTHER_PURPOSE: 'Other Purpose',
};

const PBG_ORDER = [
  'HISPANIC', 'AMERICAN_INDIAN', 'ASIAN', 'BLACK', 'NATIVE_HAWAIIAN',
  'FEMALE', 'MALE_FEMALE_JOINT', 'ELDERLY', 'MMCT',
];

const PURPOSE_ORDER = [
  'HOME_PURCHASE', 'HOME_IMPROVEMENT',
  'REFINANCING', 'CASH_OUT_REFI', 'OTHER_PURPOSE',
];

interface FldMatrixProps {
  cells: any[];
  /** The engine's tested Total cells (all purposes combined). Absent on analyses run before 2026-10-22. */
  totalCells?: any[];
  isMeanComparison?: boolean;
  /** Decimal places for mean comparison values (default: 3; use 0 for whole numbers) */
  meanDecimals?: number;
  /** Unit suffix for mean comparison values (e.g., "avg. days" for U2). */
  meanUnit?: string;
  /** Format mean values as USD currency (e.g., "$1,170.75") — used for P4 OC / P4 TLC. */
  meanCurrency?: boolean;
  /** Custom labels for rate/control in cells (e.g., "You"/"Market" for peer comparison) */
  rateLabel?: string;
  controlLabel?: string;
  onCellClick?: (cell: any) => void;
}

export default function FldMatrix({ cells, totalCells, isMeanComparison, meanDecimals, meanUnit, meanCurrency, rateLabel, controlLabel, onCellClick }: FldMatrixProps) {
  // Build a lookup map for cells
  const cellMap = new Map<string, any>();
  for (const cell of cells) {
    cellMap.set(`${cell.protectedGroup}:${cell.loanPurpose}`, cell);
  }

  const totalMap = new Map<string, any>();
  for (const cell of totalCells ?? []) totalMap.set(cell.protectedGroup, cell);

  // Determine which PBGs actually have data
  const activePbgs = PBG_ORDER.filter(pbg =>
    PURPOSE_ORDER.some(p => {
      const cell = cellMap.get(`${pbg}:${p}`);
      return cell && (cell.total > 0 || cell.controlTotal > 0);
    }),
  );

  if (activePbgs.length === 0) {
    return <p className="text-ink-3 text-sm py-4">No data available for this analysis.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr>
            <th className="text-left text-xs font-semibold text-ink-3 p-2 w-36">PBG / CG</th>
            {PURPOSE_ORDER.map(p => (
              <th key={p} className="text-center text-xs font-semibold text-ink-3 p-2">
                {PURPOSE_LABELS[p]}
              </th>
            ))}
            <th className="text-center text-xs font-semibold text-ink-3 p-2">Total</th>
          </tr>
        </thead>
        <tbody>
          {activePbgs.map(pbg => (
            <tr key={pbg} className="border-t border-rule">
              <td className="text-xs font-medium text-ink-2 p-2 whitespace-nowrap">
                {PBG_ROW_LABELS[pbg]?.pbg} / {PBG_ROW_LABELS[pbg]?.cg}
              </td>
              {PURPOSE_ORDER.map(purpose => {
                const cell = cellMap.get(`${pbg}:${purpose}`);
                return (
                  <td key={purpose} className="p-1">
                    {cell ? (
                      <FldCellBlock
                        cell={cell}
                        isMeanComparison={isMeanComparison}
                        meanDecimals={meanDecimals}
                        meanUnit={meanUnit}
                        meanCurrency={meanCurrency}
                        rateLabel={rateLabel}
                        controlLabel={controlLabel}
                        onClick={onCellClick ? () => onCellClick(cell) : undefined}
                      />
                    ) : (
                      <div className="p-2 text-center text-xs text-ink-4">—</div>
                    )}
                  </td>
                );
              })}
              <td className="p-1">
                {totalMap.get(pbg) ? (
                  <FldCellBlock
                    cell={totalMap.get(pbg)}
                    isMeanComparison={isMeanComparison}
                    meanDecimals={meanDecimals}
                    meanUnit={meanUnit}
                    meanCurrency={meanCurrency}
                    rateLabel={rateLabel}
                    controlLabel={controlLabel}
                    onClick={onCellClick ? () => onCellClick(totalMap.get(pbg)) : undefined}
                  />
                ) : (
                <FldTotalBlock
                  cells={PURPOSE_ORDER.map(purpose => cellMap.get(`${pbg}:${purpose}`)).filter(Boolean)}
                  isMeanComparison={isMeanComparison}
                  meanDecimals={meanDecimals}
                  meanUnit={meanUnit}
                  meanCurrency={meanCurrency}
                  rateLabel={rateLabel}
                  controlLabel={controlLabel}
                />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalMap.size > 0 && (
        <p className="mt-2 text-xs text-ink-3">
          Total compares each group with its control group across all loan purposes combined and is tested on that
          combined population, so its significance may differ from the individual loan purposes.
        </p>
      )}
    </div>
  );
}
